import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useGame } from '../../context/GameContext';
import { resources, buildings, technologies, BALANCE } from '../../data';
import { ResourceChip } from '../common';
import { canAfford as checkCanAfford } from '../../modules/shared';

function BuildingPanel({ onNotification, highlightedBuilding }) {
  const {
    playerResources,
    buildingCounts,
    researchedTechs,
    buildBuilding,
    getBuildingCost,
    getBuildingCapacity,
    discoveredBuildings,
    unlockedBuildings,
    // Trade Route system
    isBuildingTradeRouteEnabled,
    isStandingOnBuildingTile,
  } = useGame();

  const [showLocked, setShowLocked] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [filterByResource, setFilterByResource] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [fadingOut, setFadingOut] = useState(new Set());
  const [pinnedBuildings, setPinnedBuildings] = useState(new Set());
  const [unlockTooltip, setUnlockTooltip] = useState(null); // { buildingId, x, y }

  // Store refs to card elements for direct animation
  const cardRefs = useRef({});
  const prevAffordability = useRef({});
  const prevPositions = useRef({});
  const prevSortOrder = useRef([]);

  // Debounced re-sort: freeze sorting during rapid purchases
  const lastPurchaseTime = useRef(0);
  const RESORT_DELAY = 500; // ms - only re-sort after 500ms of no purchases
  const frozenSortOrder = useRef([]);

  // Get available buildings based on research
  const availableBuildings = Object.values(buildings).filter(building => {
    if (!building.unlockCondition) return true;
    if (building.unlockCondition.tech) {
      return researchedTechs.includes(building.unlockCondition.tech);
    }
    return true;
  });

  // Get all unique produced resources for filter dropdown
  const producedResources = [...new Set(
    availableBuildings.flatMap(b => Object.keys(b.baseProduction))
  )];

  // Filter by search query (name OR produced resource)
  let filteredBuildings = availableBuildings.filter(building => {
    const query = searchQuery.toLowerCase();
    if (!query) return true;
    if (building.name.toLowerCase().includes(query)) return true;
    const producesMatch = Object.keys(building.baseProduction).some(resId => {
      const resName = resources[resId]?.name || resId;
      return resName.toLowerCase().includes(query);
    });
    return producesMatch;
  });

  // Filter by resource production
  if (filterByResource !== 'all') {
    filteredBuildings = filteredBuildings.filter(building =>
      Object.keys(building.baseProduction).includes(filterByResource)
    );
  }

  // Get primary production resource for sorting
  const getPrimaryResource = (building) => {
    const entries = Object.entries(building.baseProduction);
    if (entries.length === 0) return '';
    return entries.sort((a, b) => b[1] - a[1])[0][0];
  };

  // Get category for sorting (based on primary resource)
  const getCategory = (building) => {
    const primaryRes = getPrimaryResource(building);
    if (!primaryRes) return 'z';
    const res = resources[primaryRes];
    return res?.category || 'z';
  };

  // Sort buildings - pinned buildings stay in place
  const sortedBuildings = (() => {
    // Check if we should freeze sorting (during rapid purchases)
    const timeSinceLastPurchase = Date.now() - lastPurchaseTime.current;
    const shouldFreezeSorting = timeSinceLastPurchase < RESORT_DELAY && frozenSortOrder.current.length > 0;

    if (shouldFreezeSorting) {
      // Return buildings in frozen order (prevents moving target during spam clicks)
      return frozenSortOrder.current
        .map(id => filteredBuildings.find(b => b.id === id))
        .filter(Boolean); // Remove any that were filtered out
    }

    // Separate pinned and unpinned buildings
    const pinned = filteredBuildings.filter(b => pinnedBuildings.has(b.id));
    const unpinned = filteredBuildings.filter(b => !pinnedBuildings.has(b.id));

    // Sort unpinned buildings
    const sortedUnpinned = unpinned.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'default':
          // Sort by: Count (desc) → Tier (asc) → Category (asc)
          const countA = buildingCounts[a.id] || 0;
          const countB = buildingCounts[b.id] || 0;
          if (countB !== countA) return countB - countA;
          if (a.tier !== b.tier) return a.tier - b.tier;
          return getCategory(a).localeCompare(getCategory(b));
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'owned':
          comparison = (buildingCounts[a.id] || 0) - (buildingCounts[b.id] || 0);
          break;
        case 'production':
          const resA = getPrimaryResource(a);
          const resB = getPrimaryResource(b);
          comparison = resA.localeCompare(resB);
          if (comparison === 0) comparison = a.tier - b.tier;
          break;
        case 'tier':
          comparison = a.tier - b.tier;
          if (comparison === 0) comparison = a.name.localeCompare(b.name);
          break;
        default:
          comparison = a.tier - b.tier;
          if (comparison === 0) comparison = a.name.localeCompare(b.name);
      }

      // Apply sort order (ascending/descending)
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    // Merge: keep pinned buildings in their original positions among all buildings
    const result = [];
    const resultIds = new Set(); // O(1) lookup instead of O(n) array.includes()
    let unpinnedIndex = 0;

    // Get original order from prevSortOrder or create initial order
    const referenceOrder = prevSortOrder.current.length > 0
      ? prevSortOrder.current
      : filteredBuildings.map(b => b.id);

    referenceOrder.forEach(id => {
      const building = filteredBuildings.find(b => b.id === id);
      if (!building) return;

      if (pinnedBuildings.has(id)) {
        result.push(building);
        resultIds.add(building.id);
      } else if (unpinnedIndex < sortedUnpinned.length) {
        // Find next unpinned building - O(1) lookup with Set
        while (unpinnedIndex < sortedUnpinned.length && resultIds.has(sortedUnpinned[unpinnedIndex].id)) {
          unpinnedIndex++;
        }
        if (unpinnedIndex < sortedUnpinned.length) {
          result.push(sortedUnpinned[unpinnedIndex]);
          resultIds.add(sortedUnpinned[unpinnedIndex].id);
          unpinnedIndex++;
        }
      }
    });

    // Add any remaining unpinned buildings - O(1) lookup with Set
    sortedUnpinned.forEach(b => {
      if (!resultIds.has(b.id)) {
        result.push(b);
        resultIds.add(b.id);
      }
    });

    // Store this as the frozen order for potential future freeze
    frozenSortOrder.current = result.map(b => b.id);

    return result;
  })();

  // Toggle pin for a building
  const togglePin = (buildingId) => {
    setPinnedBuildings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(buildingId)) {
        newSet.delete(buildingId);
      } else {
        newSet.add(buildingId);
      }
      return newSet;
    });
  };

  // Get locked buildings (not yet unlocked)
  const lockedBuildings = Object.values(buildings).filter(building => {
    if (!building.unlockCondition) return false;
    if (building.unlockCondition.tech) {
      return !researchedTechs.includes(building.unlockCondition.tech);
    }
    return false;
  });

  // Animate card using Web Animations API (handles spam gracefully)
  const animateCard = useCallback((buildingId) => {
    const card = cardRefs.current[buildingId];
    if (!card) return;

    // Cancel any existing animations and start fresh from current state
    card.getAnimations().forEach(anim => anim.cancel());

    // Pop animation
    card.animate([
      { transform: 'scale(1)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
      { transform: 'scale(1.04)', boxShadow: '0 0 16px rgba(250,204,21,0.5), 0 0 32px rgba(250,204,21,0.25)', offset: 0.3 },
      { transform: 'scale(0.98)', offset: 0.6 },
      { transform: 'scale(1)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }
    ], {
      duration: 280,
      easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
      fill: 'forwards'
    });

    // Shimmer overlay
    const shimmer = card.querySelector('.build-shimmer');
    if (shimmer) {
      shimmer.getAnimations().forEach(anim => anim.cancel());
      shimmer.animate([
        { left: '-100%', opacity: 0 },
        { left: '0%', opacity: 1, offset: 0.3 },
        { left: '100%', opacity: 0 }
      ], {
        duration: 350,
        easing: 'ease-out',
        fill: 'forwards'
      });
    }

    // Owned count flash
    const owned = card.querySelector('.bc-owned');
    if (owned) {
      owned.getAnimations().forEach(anim => anim.cancel());
      owned.animate([
        { color: '#4ade80', fontWeight: '600' },
        { color: '#4ade80', fontWeight: '600', offset: 0.7 },
        { color: '', fontWeight: '' }
      ], {
        duration: 400,
        easing: 'ease-out',
        fill: 'forwards'
      });
    }
  }, []);

  // Handle build with animation
  const handleBuild = (buildingId) => {
    const building = buildings[buildingId];

    // Record purchase time for debounced re-sort
    lastPurchaseTime.current = Date.now();

    // Store current affordability for all buildings before building
    const currentAffordability = {};
    sortedBuildings.forEach(b => {
      const bOwned = buildingCounts[b.id] || 0;
      const bCost = getBuildingCost(b, bOwned);
      currentAffordability[b.id] = Object.entries(bCost).every(
        ([res, amt]) => (playerResources[res] || 0) >= amt
      );
    });
    prevAffordability.current = currentAffordability;

    const success = buildBuilding(buildingId);
    if (success) {
      // Trigger smooth animation
      animateCard(buildingId);
      onNotification(`Built ${building.name}!`, 'success');
      
      // Check for fade-outs after a delay
      setTimeout(() => {
        const newFading = new Set();
        sortedBuildings.forEach(b => {
          const bOwned = buildingCounts[b.id] || 0 + (b.id === buildingId ? 1 : 0);
          const bCost = getBuildingCost(b, bOwned);
          const canNowAfford = Object.entries(bCost).every(
            ([res, amt]) => (playerResources[res] || 0) >= amt
          );
          if (prevAffordability.current[b.id] && !canNowAfford) {
            newFading.add(b.id);
          }
        });
        
        if (newFading.size > 0) {
          setFadingOut(newFading);
          setTimeout(() => setFadingOut(new Set()), 400);
        }
      }, 300);
    }
  };

  // Get unlock requirement text
  const getUnlockText = (building) => {
    if (!building.unlockCondition) return null;
    if (building.unlockCondition.tech) {
      const tech = technologies[building.unlockCondition.tech];
      return tech ? `${tech.icon} ${tech.name}` : building.unlockCondition.tech;
    }
    return null;
  };

  // Scroll to highlighted building
  useEffect(() => {
    if (highlightedBuilding && cardRefs.current[highlightedBuilding]) {
      setTimeout(() => {
        cardRefs.current[highlightedBuilding]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [highlightedBuilding]);

  // FLIP animation for position changes (subtle) - only when sort order actually changes
  useEffect(() => {
    // Check if the sort order actually changed (compare building IDs in order)
    const currentSortOrder = sortedBuildings.map(b => b.id);
    const sortOrderChanged = JSON.stringify(currentSortOrder) !== JSON.stringify(prevSortOrder.current);

    if (!sortOrderChanged) {
      // Sort order didn't change, just update positions without animating
      const newPositions = {};
      sortedBuildings.forEach((building) => {
        const card = cardRefs.current[building.id];
        if (card) {
          const rect = card.getBoundingClientRect();
          newPositions[building.id] = { top: rect.top, left: rect.left };
        }
      });
      prevPositions.current = newPositions;
      prevSortOrder.current = currentSortOrder;
      return;
    }

    // Sort order changed - capture old positions before DOM updates
    const oldPositions = { ...prevPositions.current };

    // Use a more robust approach: wait for React render + browser paint
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Add a tiny delay to ensure paint is complete
        setTimeout(() => {
          const newPositions = {};

          // Record new positions after DOM update
          sortedBuildings.forEach((building) => {
            const card = cardRefs.current[building.id];
            if (card) {
              const rect = card.getBoundingClientRect();
              newPositions[building.id] = { top: rect.top, left: rect.left };
            }
          });

          // Collect all buildings that moved
          const movingBuildings = [];
          sortedBuildings.forEach((building, index) => {
            const card = cardRefs.current[building.id];
            const oldPos = oldPositions[building.id];
            const newPos = newPositions[building.id];

            if (card && oldPos && newPos) {
              const deltaX = oldPos.left - newPos.left;
              const deltaY = oldPos.top - newPos.top;

              // Only animate if position changed significantly
              if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
                movingBuildings.push({
                  card,
                  deltaX,
                  deltaY,
                  index
                });
              }
            }
          });

          if (movingBuildings.length === 0) {
            // No movement, just save positions
            prevPositions.current = newPositions;
            prevSortOrder.current = currentSortOrder;
            return;
          }

          // Synchronized animation: all buildings wait briefly, then move together
          movingBuildings.forEach(({ card, deltaX, deltaY }) => {
            // Cancel any existing animations
            card.getAnimations().forEach(anim => {
              if (anim.effect?.target === card) anim.cancel();
            });

            // Set initial state immediately (all at once, no stagger)
            card.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
            card.style.transition = 'none';
            card.style.opacity = '0.85';
          });

          // Force reflow once for all cards
          if (movingBuildings.length > 0) {
            movingBuildings[0].card.offsetHeight;
          }

          // Animate ALL buildings together after brief pause
          requestAnimationFrame(() => {
            movingBuildings.forEach(({ card }) => {
              card.style.transform = '';
              card.style.opacity = '';
              card.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s ease-out';
            });
          });

          // Save positions and sort order for next time
          prevPositions.current = newPositions;
          prevSortOrder.current = currentSortOrder;
        }, 0);
      });
    });
  }, [sortedBuildings]);

  return (
    <div className="center-panel">
      <style>{`
        @keyframes fade-to-locked {
          0% {
            opacity: 1;
            filter: saturate(1) brightness(1);
          }
          100% {
            opacity: 0.65;
            filter: saturate(0.6) brightness(0.9);
          }
        }

        @keyframes highlight-pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(250, 204, 21, 0.7),
                        0 0 20px rgba(250, 204, 21, 0.4);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(250, 204, 21, 0),
                        0 0 30px rgba(250, 204, 21, 0.6);
          }
        }

        .building-card {
          position: relative;
          overflow: hidden;
        }

        .building-card.fading-out {
          animation: fade-to-locked 0.4s ease-out forwards;
        }

        .building-card.highlighted {
          animation: highlight-pulse 2s ease-in-out;
          border: 2px solid #facc15 !important;
          background: linear-gradient(135deg, rgba(250, 204, 21, 0.1) 0%, rgba(250, 204, 21, 0.05) 100%) !important;
        }

        .build-shimmer {
          position: absolute;
          top: 0;
          left: -100%;
          width: 60%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.35),
            transparent
          );
          pointer-events: none;
          z-index: 10;
        }
      `}</style>

      <div className="panel-header">
        <h3>🏗️ Buildings</h3>
        <div className="panel-header-right">
          <div className="building-sort-controls">
            <input
              type="text"
              className="building-search-input"
              placeholder="🔍 Name or resource..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <select
              className="building-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="default">Sort: Default</option>
              <option value="tier">Sort: Tier</option>
              <option value="name">Sort: Name</option>
              <option value="owned">Sort: Owned</option>
              <option value="production">Sort: Resource</option>
            </select>
            <button
              className="sort-order-toggle"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              title={sortOrder === 'asc' ? 'Ascending (click for Descending)' : 'Descending (click for Ascending)'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
            <select 
              className="building-sort-select"
              value={filterByResource}
              onChange={(e) => setFilterByResource(e.target.value)}
            >
              <option value="all">All Resources</option>
              {producedResources.map(resId => (
                <option key={resId} value={resId}>
                  {resources[resId]?.icon} {resources[resId]?.name || resId}
                </option>
              ))}
            </select>
          </div>
          <span className="badge">{filteredBuildings.length}</span>
          {lockedBuildings.length > 0 && (
            <button 
              className={`locked-toggle ${showLocked ? 'active' : ''}`}
              onClick={() => setShowLocked(!showLocked)}
            >
              🔒 {lockedBuildings.length}
            </button>
          )}
        </div>
      </div>

      <div className="buildings-grid">
        {sortedBuildings.map(building => {
          const owned = buildingCounts[building.id] || 0;
          const cost = getBuildingCost(building, owned);
          const canAfford = checkCanAfford(cost, playerResources);
          const isFading = fadingOut.has(building.id);
          const isHighlighted = highlightedBuilding === building.id;

          // Get capacity info for tile-based limits
          const capacityInfo = getBuildingCapacity(building.id);
          const isAtCapacity = !capacityInfo.canBuild;

          // Determine brightness state
          let brightnessState = 'state-none'; // Default: don't own any, can't afford
          if (owned > 0) {
            brightnessState = 'state-owned'; // Own at least one - always bright
          } else if (canAfford) {
            brightnessState = 'state-can-afford'; // Don't own but can afford - blue sheen
          }

          const isDiscovered = discoveredBuildings.includes(building.id);
          const isUnlocked = unlockedBuildings.includes(building.id);
          const isOnTile = isStandingOnBuildingTile(building.id);
          const isTradeRouteConnected = isBuildingTradeRouteEnabled(building.id);
          // Can only mass produce if: unlocked (built on map) AND (standing on tile OR trade route connected)
          const canMassProduce = isUnlocked && (isOnTile || isTradeRouteConnected);
          // Show tooltip if can't mass produce OR if there's capacity info to show
          const showTooltip = !canMassProduce || capacityInfo.capacity > 0;

          return (
            <div
              key={building.id}
              ref={el => cardRefs.current[building.id] = el}
              className={`building-card t${building.tier} ${brightnessState} ${!canAfford && owned === 0 ? 'locked' : ''} ${isFading ? 'fading-out' : ''} ${isHighlighted ? 'highlighted' : ''}`}
              onMouseEnter={(e) => {
                if (showTooltip) {
                  setUnlockTooltip({
                    buildingId: building.id,
                    buildingName: building.name,
                    x: e.clientX,
                    y: e.clientY,
                    isDiscovered,
                    isUnlocked,
                    isOnTile,
                    isTradeRouteConnected,
                    capacityInfo,
                    canMassProduce
                  });
                }
              }}
              onMouseMove={(e) => {
                if (showTooltip && unlockTooltip?.buildingId === building.id) {
                  setUnlockTooltip({
                    buildingId: building.id,
                    buildingName: building.name,
                    x: e.clientX,
                    y: e.clientY,
                    isDiscovered,
                    isUnlocked,
                    isOnTile,
                    isTradeRouteConnected,
                    capacityInfo,
                    canMassProduce
                  });
                }
              }}
              onMouseLeave={() => {
                if (unlockTooltip?.buildingId === building.id) {
                  setUnlockTooltip(null);
                }
              }}
            >
              {/* Shimmer overlay element */}
              <div className="build-shimmer" />

              {/* Top Row: Icon, Name, Badges */}
              <div className="bc-header-grid">
                <span className="bc-icon-large">{building.icon}</span>
                <div className="bc-title-area">
                  <div className="bc-name">{building.name}</div>
                  <div className="bc-meta">
                    <span className={`bc-owned ${isAtCapacity ? 'at-capacity' : ''}`}>
                      {capacityInfo.maxOwned > 0
                        ? `${capacityInfo.owned}/${capacityInfo.maxOwned}`
                        : `×${owned}`
                      }
                    </span>
                    <span className="bc-tier">T{building.tier}</span>
                  </div>
                </div>
                <button
                  className={`bc-pin-btn ${pinnedBuildings.has(building.id) ? 'pinned' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePin(building.id);
                  }}
                  title={pinnedBuildings.has(building.id) ? 'Unpin (allow sorting)' : 'Pin in place'}
                >
                  {pinnedBuildings.has(building.id) ? '📌' : '📍'}
                </button>
              </div>

              {/* Production Grid */}
              <div className="bc-production-grid">
                <div className="bc-section-label">Production</div>
                <div className="bc-prod-chips">
                  {Object.entries(building.baseProduction).map(([res, amt]) => {
                    const resName = resources[res]?.name || res;
                    return (
                      <span key={res} className="bc-chip prod" title={`${resName}: +${amt.toFixed(2)}/s per building`}>
                        {resources[res]?.icon} {amt.toFixed(1)}/s
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Cost Grid */}
              <div className="bc-cost-grid">
                <div className="bc-section-label">Cost</div>
                <div className="bc-cost-chips">
                  {Object.entries(cost).map(([res, amt]) => (
                    <ResourceChip
                      key={res}
                      resourceId={res}
                      amount={Math.floor(amt)}
                      currentAmount={playerResources[res] || 0}
                      canAfford={(playerResources[res] || 0) >= amt}
                      size="small"
                    />
                  ))}
                </div>
              </div>

              {/* Discovery Status Grid */}
              <div className="bc-status-grid">
                <div className="bc-status-item">
                  <span className="bc-status-icon">{discoveredBuildings.includes(building.id) ? '✅' : '⬜'}</span>
                  <span className="bc-status-text">Discovered</span>
                </div>
                <div className="bc-status-item">
                  <span className="bc-status-icon">{unlockedBuildings.includes(building.id) ? '✅' : '⬜'}</span>
                  <span className="bc-status-text">Built on map</span>
                </div>
              </div>

              {/* Helper Text */}
              <div className="bc-unlock-helper">
                {!isDiscovered
                  ? '🔍 Find this biome on the World Map first'
                  : !isUnlocked
                    ? '🏗️ Go to World Map → Stand on matching biome → Build here'
                    : isOnTile
                      ? '✓ Ready to mass-produce!'
                      : isTradeRouteConnected
                        ? '🛤️ Trade Route active - ready to mass-produce!'
                        : '📍 Travel to this tile, or connect it with a Trade Route'}
              </div>

              {/* Action Buttons Grid */}
              <div className="bc-buttons-grid single">
                {/* Mass production button - enabled if unlocked AND (standing on tile OR trade-route-connected) AND has capacity */}
                {(() => {
                  // Must be unlocked (built on map) AND either standing on tile or trade route connected AND have capacity
                  const canBuildHere = isUnlocked && (isOnTile || isTradeRouteConnected);
                  const buttonDisabled = !canAfford || !canBuildHere || isAtCapacity;
                  let buttonText = '🔨 Build';

                  if (!isDiscovered) {
                    buttonText = '🔒 Not Discovered';
                  } else if (!isUnlocked) {
                    buttonText = '🔒 Not Built';
                  } else if (!isOnTile && !isTradeRouteConnected) {
                    buttonText = '🔒 No Access';
                  } else if (isAtCapacity) {
                    buttonText = '🏗️ At Capacity';
                  } else if (!canAfford) {
                    buttonText = '💰 Need Resources';
                  }

                  return (
                    <button
                      className="bc-btn-primary"
                      onClick={() => handleBuild(building.id)}
                      disabled={buttonDisabled}
                    >
                      {buttonText}
                    </button>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Locked Buildings Section */}
      {showLocked && lockedBuildings.length > 0 && (
        <div className="locked-buildings-section">
          <div className="locked-header">🔒 Locked Buildings</div>
          <div className="locked-grid">
            {lockedBuildings.map(building => (
              <div key={building.id} className="locked-building-card">
                <span className="lb-icon">{building.icon}</span>
                <div className="lb-info">
                  <div className="lb-name">{building.name}</div>
                  <div className="lb-unlock">🔬 {getUnlockText(building)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Building Info Tooltip */}
      {unlockTooltip && ReactDOM.createPortal(
        <BuildingInfoTooltip
          buildingName={unlockTooltip.buildingName}
          isDiscovered={unlockTooltip.isDiscovered}
          isUnlocked={unlockTooltip.isUnlocked}
          isOnTile={unlockTooltip.isOnTile}
          isTradeRouteConnected={unlockTooltip.isTradeRouteConnected}
          capacityInfo={unlockTooltip.capacityInfo}
          canMassProduce={unlockTooltip.canMassProduce}
          x={unlockTooltip.x}
          y={unlockTooltip.y}
        />,
        document.body
      )}
    </div>
  );
}

/**
 * BuildingInfoTooltip - Shows info about why mass production is locked, how to enable it, and capacity info
 */
function BuildingInfoTooltip({ buildingName, isDiscovered, isUnlocked, isOnTile, isTradeRouteConnected, capacityInfo, canMassProduce, x, y }) {
  const tooltipWidth = 320;
  const tooltipHeight = 220;

  let left = x + 15;
  let top = y - 10;

  // Keep tooltip on screen
  if (left + tooltipWidth > window.innerWidth - 10) left = x - tooltipWidth - 15;
  if (left < 10) left = 10;
  if (top + tooltipHeight > window.innerHeight - 10) top = window.innerHeight - tooltipHeight - 10;
  if (top < 10) top = 10;

  // Render capacity section with explanation per DESIGN.md
  const renderCapacitySection = () => {
    if (!capacityInfo || capacityInfo.capacity === 0) {
      return (
        <div className="but-capacity-section">
          <div className="but-capacity-row">
            <span className="but-capacity-label">📊 Capacity:</span>
            <span className="but-capacity-none">0/0</span>
          </div>
          <div className="but-capacity-explain">
            Each tile building grants <strong>5 panel slots</strong>. Build on more tiles to increase capacity.
          </div>
        </div>
      );
    }

    const isAtCapacity = capacityInfo.current >= capacityInfo.capacity;

    return (
      <div className="but-capacity-section">
        <div className="but-capacity-row">
          <span className="but-capacity-label">📊 Owned:</span>
          <span className={`but-capacity-value ${isAtCapacity ? 'at-capacity' : ''}`}>
            {capacityInfo.owned}/{capacityInfo.maxOwned}
          </span>
          <span className="but-capacity-calc">
            ({capacityInfo.tilesBuilt} tile{capacityInfo.tilesBuilt !== 1 ? 's' : ''} × 6)
          </span>
          {isAtCapacity && <span className="but-capacity-warn">⚠️</span>}
          {!isAtCapacity && capacityInfo.remaining > 0 && (
            <span className="but-capacity-avail">+{capacityInfo.remaining}</span>
          )}
        </div>
        <div className="but-capacity-explain">
          {isAtCapacity
            ? <>Build more <strong>{buildingName}</strong> on World Map tiles to increase capacity.</>
            : <>Each tile grants <strong>1 building + 5 panel slots</strong>. You can build {capacityInfo.remaining} more here.</>
          }
        </div>
      </div>
    );
  };

  // Determine which state we're in and show appropriate guidance
  const renderContent = () => {
    // State 1: Not discovered yet
    if (!isDiscovered) {
      return (
        <>
          <div className="but-header">
            <span className="but-icon">🔍</span>
            <span className="but-title">Find This Biome</span>
          </div>
          <p className="but-message">
            This building is linked to a <strong>biome you haven't found yet</strong>.
          </p>
          <div className="but-steps">
            <div className="but-step">1. Go to <strong>World Map</strong> tab</div>
            <div className="but-step">2. Click adjacent tiles to explore</div>
            <div className="but-step">3. Each biome unlocks different buildings</div>
          </div>
        </>
      );
    }

    // State 2: Discovered but not built on map yet
    if (!isUnlocked) {
      return (
        <>
          <div className="but-header">
            <span className="but-icon">🏗️</span>
            <span className="but-title">Build It First</span>
          </div>
          <p className="but-message">
            ✅ Biome found! Now <strong>construct this building</strong> on that tile.
          </p>
          <div className="but-steps">
            <div className="but-step">1. Go to <strong>World Map</strong> tab</div>
            <div className="but-step">2. Move to a tile with the right biome</div>
            <div className="but-step">3. Use the <strong>Build</strong> section to construct it</div>
          </div>
        </>
      );
    }

    // State 3: Built on map, but not standing on tile and no trade route
    if (!canMassProduce) {
      return (
        <>
          <div className="but-header">
            <span className="but-icon">📍</span>
            <span className="but-title">Need Access</span>
          </div>
          <p className="but-message">
            You've built this, but need to <strong>be there</strong> or have a <strong>Trade Route</strong>.
          </p>

          <div className="but-divider"></div>

          <div className="but-section">
            <div className="but-section-title">Option 1: Travel There</div>
            <div className="but-hint">Go to World Map and walk to the tile with this building.</div>
          </div>

          <div className="but-section">
            <div className="but-section-title">Option 2: Trade Route</div>
            <div className="but-hint">Permanently unlock by connecting to your Kingdom.</div>
            <div className="but-steps">
              <div className="but-step">1. Stand on the tile with this building</div>
              <div className="but-step">2. Click <strong>"Build Route"</strong> button</div>
              <div className="but-step">3. Route connects back to Kingdom (0,0)</div>
            </div>
          </div>

          <div className="but-section">
            <div className="but-section-title">💡 Tip</div>
            <div className="but-hint">Click 🛤️ on World Map controls to see routes.</div>
          </div>

          {renderCapacitySection()}
        </>
      );
    }

    // State 4: Can mass produce - just show capacity info
    return renderCapacitySection();
  };

  return (
    <div
      className="building-unlock-tooltip"
      style={{ left, top }}
    >
      {renderContent()}
    </div>
  );
}

export default BuildingPanel;
